import { LuvImage, Modal } from '@/components/ui';
import { TypeImage, TypeProduct, TypeVideo } from '@/core/generated-types';
import React, { FC } from 'react';
import { CarouselProducts } from '../Carousel';
import {
  ShopifyProduct,
  ShopifyVariant,
  getProductInventoryStatus,
} from '@/lib/shopify';
import classNames from 'classnames';
import { ORGANIC_COTTON_BABY_DOLL_COLOR_MAP } from '@/utils/products';
import { AddButton, InventoryStatus } from '../InventoryStatus';
import { RichText } from '../RichText';
import { Document } from '@contentful/rich-text-types';
import { TypeProduct as InternalProduct } from '@/lib/api/productCatalog/types';

interface LookInsideModalProps {
  isOpen: boolean;
  handleClose: () => void;
  product: TypeProduct;
  addProduct: () => void;
  removeProduct: () => void;
  isSelected?: boolean;
  shopifyProduct: ShopifyProduct | null;
  selectedVariant?: ShopifyVariant | null;
  setSelectedVariant?: (option: ShopifyVariant) => void;
  internalProduct?: InternalProduct;
}

const LookInsideModal: FC<LookInsideModalProps> = ({
  isOpen,
  handleClose,
  product,
  addProduct,
  removeProduct,
  isSelected,
  shopifyProduct,
  selectedVariant,
  setSelectedVariant,
  internalProduct,
}) => {
  const {
    fields: {
      title,
      featuredImage,
      longDescription,
      productDetails,
      productImages,
    },
  } = product as TypeProduct;
  const defaultVariant = (shopifyProduct as ShopifyProduct)?.variants[0];
  const isOrganicCottonBabyDoll =
    shopifyProduct?.title === 'Organic Cotton Baby Doll';
  const organicCottonBabyDollOptions = shopifyProduct?.variants;
  const inventoryStatus = getProductInventoryStatus(
    shopifyProduct as ShopifyProduct,
    selectedVariant as ShopifyVariant,
  );
  const descriptionContent = longDescription || internalProduct?.description;

  return (
    <Modal isOpen={isOpen} handleClose={handleClose}>
      <div className="container max-w-sm lg:grid lg:max-w-[936px] lg:grid-cols-2 lg:pt-6">
        <div>
          {productImages ? (
            <CarouselProducts
              name="look inside carousel"
              components={productImages as (TypeVideo | TypeImage)[]}
            />
          ) : (
            <LuvImage
              imageData={featuredImage}
              alt={featuredImage.fields.altText}
            />
          )}
        </div>
        <div>
          <div className="mb-2 flex justify-between text-lg font-bold">
            <p>{title}</p>
            <p>{defaultVariant?.formattedPrice}</p>
          </div>
          {productDetails ? (
            <RichText content={productDetails as Document} />
          ) : (
            descriptionContent && (
              <p
                className="mb-2"
                dangerouslySetInnerHTML={{ __html: descriptionContent }}
              />
            )
          )}
          {isOrganicCottonBabyDoll && (
            <div className="mt-5 border-t border-grey-midtone pt-5">
              <p className="mb-2">
                Choose a skin tone:{' '}
                {selectedVariant && (
                  <span className="capitalize">{selectedVariant.title}</span>
                )}
              </p>
              <ul className="flex">
                {organicCottonBabyDollOptions?.map((option) => {
                  const { id } = option;
                  const isOrganicCottonBabyDollSkinToneSelected =
                    selectedVariant?.id === id;
                  return (
                    <li
                      key={id}
                      className={classNames(
                        'mr-2 flex h-[40px] w-[40px] cursor-pointer items-center justify-center rounded-full border',
                        isOrganicCottonBabyDollSkinToneSelected
                          ? 'border-charcoal'
                          : 'border-transparent',
                      )}
                      onClick={() => {
                        if (setSelectedVariant !== undefined) {
                          setSelectedVariant(option);
                        }
                      }}
                    >
                      <div
                        className="h-[33px] w-[33px] rounded-full"
                        style={{
                          backgroundColor:
                            ORGANIC_COTTON_BABY_DOLL_COLOR_MAP[option.title],
                        }}
                      />
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          <div className="mt-5 border-t border-grey-midtone pt-5">
            <InventoryStatus
              inventoryStatus={inventoryStatus}
              product={product}
            />
            <AddButton
              inventoryStatus={inventoryStatus}
              onClick={() => {
                handleClose();
                isSelected ? removeProduct() : addProduct();
              }}
              isSelected={isSelected}
              product={product}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default LookInsideModal;
